// useFeedbackSubmit: encapsulates the network + state lifecycle of submitting a
// single per-section feedback row to /api/gooseberry/feedback. Extracted from
// SectionFeedbackBar so that component's cyclomatic complexity drops from 16 to
// ~6 (just rendering branches) and the network logic is testable in isolation.
import { useCallback, useState } from "react";
import { toast } from "sonner";
import { feedbackService } from "@/lib/api";
import { logger } from "@/lib/logger";

/**
 * @param {object} cfg
 * @param {string} cfg.runId
 * @param {string} cfg.sectionKey
 * @param {string} cfg.sectionTitle
 * @param {string} cfg.projectName
 * @param {string} cfg.mode
 * @param {string} cfg.language
 * @param {object | null} cfg.existing - if the user has already voted this session
 * @param {(item: any) => void} cfg.onSubmitted
 *
 * @returns {{
 *   pendingVote: "up"|"down"|null,
 *   submitted: { vote, comment } | null,
 *   setSubmitted: (v: any) => void,
 *   submit: (vote: "up"|"down", commentText: string) => Promise<void>,
 * }}
 */
export function useFeedbackSubmit({
  runId,
  sectionKey,
  sectionTitle,
  projectName,
  mode,
  language = "en",
  existing,
  onSubmitted,
}) {
  const [pendingVote, setPendingVote] = useState(null);
  const [submitted, setSubmitted] = useState(existing || null);

  const submit = useCallback(
    async (vote, commentText) => {
      setPendingVote(vote);
      try {
        const payload = {
          runId,
          sectionKey,
          sectionTitle,
          projectName,
          mode,
          vote,
          comment: commentText || "",
          userAgent:
            typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 200) : "",
        };
        const item = await feedbackService.submitSectionFeedback(payload);
        setSubmitted({ vote: item.vote, comment: item.comment });
        if (vote === "up") {
          toast.success(language === "nl" ? "Bedankt voor je feedback" : "Thanks for the feedback");
        } else {
          toast.success(
            commentText
              ? (language === "nl" ? "Correctie verstuurd, je kunt nu opnieuw genereren" : "Correction sent, you can regenerate now")
              : (language === "nl" ? "Feedback opgeslagen, voeg een notitie toe om opnieuw genereren te activeren" : "Feedback noted, add a note to enable regenerate")
          );
        }
        if (typeof onSubmitted === "function") onSubmitted(item);
      } catch (err) {
        logger.error("submitSectionFeedback failed", err);
        toast.error(language === "nl" ? "Kon je feedback niet opslaan" : "Could not save your feedback");
      } finally {
        setPendingVote(null);
      }
    },
    [runId, sectionKey, sectionTitle, projectName, mode, language, onSubmitted]
  );

  return { pendingVote, submitted, setSubmitted, submit };
}
